import { operatingIndustryToUrlSlug } from "./operatingIndustryUrlSlug";

export const PAGE_CTA_PAGE_KEYS = [
  "COMPANY_PROFILE",
  "DISCOVER",
  "HOME",
  "ABOUT",
  "PRICING",
  "EXPERTS",
  "CORRIDORS",
  "MY_CLUSTER",
  "SHORTLIST",
] as const;

export type PageCtaPageKey = (typeof PAGE_CTA_PAGE_KEYS)[number];

export const PAGE_CTA_AUDIENCES = ["GUESTS", "LOGGED_IN", "BOTH"] as const;
export type PageCtaAudience = (typeof PAGE_CTA_AUDIENCES)[number];

export const PAGE_CTA_FORM_AUDIENCES = ["GUESTS", "LOGGED_IN", "BOTH"] as const;
export type PageCtaFormAudience = (typeof PAGE_CTA_FORM_AUDIENCES)[number];

export type PageCtaLayout = "banner" | "sidebar";

export type PageCtaSlotDef = {
  key: string;
  label: string;
  layout: PageCtaLayout;
};

export const PAGE_CTA_PAGE_LABELS: Record<PageCtaPageKey, string> = {
  COMPANY_PROFILE: "Company profile",
  DISCOVER: "Discover",
  HOME: "Home",
  ABOUT: "About",
  PRICING: "Pricing",
  EXPERTS: "Experts listing",
  CORRIDORS: "Clusters and corridors",
  MY_CLUSTER: "My Cluster",
  SHORTLIST: "Shortlist",
};

export const PAGE_CTA_SLOTS: Record<PageCtaPageKey, PageCtaSlotDef[]> = {
  COMPANY_PROFILE: [
    { key: "MAIN_BANNER", label: "Main column banner", layout: "banner" },
    { key: "SIDEBAR", label: "Sidebar card", layout: "sidebar" },
  ],
  DISCOVER: [
    { key: "TOP_BANNER", label: "Header (above listing)", layout: "banner" },
    { key: "AFTER_FIRST_ROW", label: "After first row of cards", layout: "banner" },
  ],
  HOME: [{ key: "TOP_BANNER", label: "Top banner", layout: "banner" }],
  ABOUT: [{ key: "PAGE_FOOTER", label: "Page footer", layout: "banner" }],
  PRICING: [{ key: "PAGE_FOOTER", label: "Page footer", layout: "banner" }],
  EXPERTS: [
    { key: "TOP_BANNER", label: "Header (above listing)", layout: "banner" },
    { key: "AFTER_FIRST_ROW", label: "After first row of cards", layout: "banner" },
  ],
  CORRIDORS: [
    { key: "TOP_BANNER", label: "Header (above listing)", layout: "banner" },
    { key: "AFTER_FIRST_ROW", label: "After first row of cards", layout: "banner" },
  ],
  MY_CLUSTER: [{ key: "TOP_BANNER", label: "Top banner", layout: "banner" }],
  SHORTLIST: [{ key: "TOP_BANNER", label: "Top banner", layout: "banner" }],
};

const GUEST_PLACEMENT_PAGES: PageCtaPageKey[] = [
  "DISCOVER",
  "ABOUT",
  "PRICING",
  "EXPERTS",
  "CORRIDORS",
  "COMPANY_PROFILE",
];

const LOGGED_IN_PLACEMENT_PAGES: PageCtaPageKey[] = [
  "HOME",
  "DISCOVER",
  "EXPERTS",
  "CORRIDORS",
  "COMPANY_PROFILE",
  "MY_CLUSTER",
  "SHORTLIST",
];

const BOTH_PLACEMENT_PAGES: PageCtaPageKey[] = GUEST_PLACEMENT_PAGES.filter((key) =>
  (LOGGED_IN_PLACEMENT_PAGES as readonly string[]).includes(key),
);

export const PAGE_CTA_SLOT_AFTER_FIRST_ROW = "AFTER_FIRST_ROW";
export const PAGE_CTA_SLOT_TOP_BANNER = "TOP_BANNER";

export const PAGE_CTA_CUSTOM_DESTINATION = "__custom__";
export const PAGE_CTA_PROFILE_TAB_NONE = "__tab__";

export type PageCtaProfileKind = "CORRIDOR" | "EXPERT";

export type PageCtaDestinationMatch = {
  destinationSelect: string;
  profileId: string | null;
  profileCompanyId: string | null;
};

export type PageCtaDestination = {
  value: string;
  label: string;
  path: string | null;
};

export const PAGE_CTA_GUEST_DESTINATIONS: PageCtaDestination[] = [
  { value: "/discover", label: "Discover companies", path: "/discover" },
  { value: "/discover?tab=experts", label: "Experts", path: "/discover?tab=experts" },
  { value: "/discover?tab=corridors", label: "Clusters and corridors", path: "/discover?tab=corridors" },
  { value: "/about", label: "About", path: "/about" },
  { value: "/pricing", label: "Pricing", path: "/pricing" },
  { value: "/login", label: "Login", path: "/login" },
  { value: "/signup", label: "Sign up", path: "/signup" },
  { value: PAGE_CTA_CUSTOM_DESTINATION, label: "Custom link (profile / URL)", path: null },
];

export const PAGE_CTA_LOGGED_IN_DESTINATIONS: PageCtaDestination[] = [
  { value: "/home", label: "Home", path: "/home" },
  { value: "/my-discovery", label: "Explore companies", path: "/my-discovery" },
  { value: "/my-discovery?tab=experts", label: "Experts", path: "/my-discovery?tab=experts" },
  { value: "/my-discovery?tab=corridors", label: "Clusters and corridors", path: "/my-discovery?tab=corridors" },
  { value: "/my-cluster", label: "My Cluster", path: "/my-cluster" },
  { value: "/profile", label: "My Profile", path: "/profile" },
  { value: "/shortlist", label: "Shortlist", path: "/shortlist" },
  { value: "/collaborations", label: "Collaborations", path: "/collaborations" },
  { value: PAGE_CTA_CUSTOM_DESTINATION, label: "Custom link (profile / URL)", path: null },
];

export const PAGE_CTA_DEFAULTS = {
  backgroundColor: "#04305c",
  titleColor: "#ffffff",
  descriptionColor: "#e8eef5",
  buttonTextColor: "#04305c",
  buttonBackgroundColor: "#ffc107",
  titleFontSize: 22,
  titleFontWeight: 700,
  descriptionFontSize: 14,
  buttonFontSize: 14,
  buttonXPct: 50,
  buttonYPct: 80,
} as const;

export function isPageCtaPageKey(value: string): value is PageCtaPageKey {
  return (PAGE_CTA_PAGE_KEYS as readonly string[]).includes(value);
}

export function isPageCtaAudience(value: string): value is PageCtaAudience {
  return (PAGE_CTA_AUDIENCES as readonly string[]).includes(value);
}

export function isPageCtaFormAudience(value: string): value is PageCtaFormAudience {
  return (PAGE_CTA_FORM_AUDIENCES as readonly string[]).includes(value);
}

export function getPageCtaSlots(pageKey: string): PageCtaSlotDef[] {
  if (!isPageCtaPageKey(pageKey)) return [];
  return PAGE_CTA_SLOTS[pageKey];
}

export function assertPageCtaSlot(pageKey: string, slotKey: string): PageCtaSlotDef {
  const slot = getPageCtaSlots(pageKey).find((s) => s.key === slotKey);
  if (!slot) {
    throw new Error(`Unknown CTA slot "${slotKey}" for page "${pageKey}"`);
  }
  return slot;
}

export function getPageCtaSlotLayout(pageKey: string, slotKey: string): PageCtaLayout {
  return assertPageCtaSlot(pageKey, slotKey).layout;
}

export type PageCtaImageSpec = {
  widthPx: number;
  heightPx: number;
  label: string;
};

const WIDE_LEADERBOARD: PageCtaImageSpec = {
  widthPx: 1120,
  heightPx: 180,
  label: "wide banner",
};

export const EXPLORE_CTA_FRAME: PageCtaImageSpec = {
  widthPx: 869,
  heightPx: 128,
  label: "centered explore banner",
};

const EXPLORE_CTA_PAGE_KEYS: readonly PageCtaPageKey[] = ["DISCOVER", "EXPERTS", "CORRIDORS"];

export function isExploreCtaPageKey(pageKey: string): boolean {
  return (EXPLORE_CTA_PAGE_KEYS as readonly string[]).includes(pageKey);
}

export const PAGE_CTA_IMAGE_SPECS: Record<PageCtaPageKey, Record<string, PageCtaImageSpec>> = {
  HOME: { TOP_BANNER: WIDE_LEADERBOARD },
  DISCOVER: { TOP_BANNER: EXPLORE_CTA_FRAME, AFTER_FIRST_ROW: EXPLORE_CTA_FRAME },
  EXPERTS: { TOP_BANNER: EXPLORE_CTA_FRAME, AFTER_FIRST_ROW: EXPLORE_CTA_FRAME },
  CORRIDORS: { TOP_BANNER: EXPLORE_CTA_FRAME, AFTER_FIRST_ROW: EXPLORE_CTA_FRAME },
  MY_CLUSTER: { TOP_BANNER: WIDE_LEADERBOARD },
  SHORTLIST: { TOP_BANNER: WIDE_LEADERBOARD },
  ABOUT: { PAGE_FOOTER: WIDE_LEADERBOARD },
  PRICING: { PAGE_FOOTER: WIDE_LEADERBOARD },
  COMPANY_PROFILE: {
    MAIN_BANNER: { widthPx: 760, heightPx: 200, label: "profile main column" },
    SIDEBAR: { widthPx: 300, heightPx: 220, label: "sidebar card" },
  },
};

const WIDE_MOBILE: PageCtaImageSpec = {
  widthPx: 390,
  heightPx: 240,
  label: "mobile banner",
};

export const PAGE_CTA_MOBILE_IMAGE_SPECS: Record<PageCtaPageKey, Record<string, PageCtaImageSpec>> = {
  HOME: { TOP_BANNER: WIDE_MOBILE },
  DISCOVER: { TOP_BANNER: WIDE_MOBILE, AFTER_FIRST_ROW: WIDE_MOBILE },
  EXPERTS: { TOP_BANNER: WIDE_MOBILE, AFTER_FIRST_ROW: WIDE_MOBILE },
  CORRIDORS: { TOP_BANNER: WIDE_MOBILE, AFTER_FIRST_ROW: WIDE_MOBILE },
  MY_CLUSTER: { TOP_BANNER: WIDE_MOBILE },
  SHORTLIST: { TOP_BANNER: WIDE_MOBILE },
  ABOUT: { PAGE_FOOTER: WIDE_MOBILE },
  PRICING: { PAGE_FOOTER: WIDE_MOBILE },
  COMPANY_PROFILE: {
    MAIN_BANNER: { widthPx: 390, heightPx: 200, label: "mobile profile main" },
    SIDEBAR: { widthPx: 390, heightPx: 240, label: "mobile sidebar card" },
  },
};

export function getPageCtaImageSpec(pageKey: string, slotKey: string): PageCtaImageSpec {
  if (!isPageCtaPageKey(pageKey)) return WIDE_LEADERBOARD;
  return PAGE_CTA_IMAGE_SPECS[pageKey]?.[slotKey] ?? WIDE_LEADERBOARD;
}

export function getPageCtaMobileImageSpec(pageKey: string, slotKey: string): PageCtaImageSpec {
  if (!isPageCtaPageKey(pageKey)) return WIDE_MOBILE;
  return PAGE_CTA_MOBILE_IMAGE_SPECS[pageKey]?.[slotKey] ?? WIDE_MOBILE;
}

export type PageCtaBoxSize = {
  widthPx: number;
  heightPx: number;
  /** When true, the box stretches to the slot width and keeps this aspect ratio. Explore CTAs stay centered at the spec size instead. */
  fullWidth: boolean;
};

/** Admin box size, falling back to the slot's recommended frame. */
export function resolvePageCtaBoxSize(item: {
  pageKey: string;
  slotKey: string;
  widthPx?: number | null;
  heightPx?: number | null;
}): PageCtaBoxSize {
  const spec = getPageCtaImageSpec(item.pageKey, item.slotKey);
  const heightPx = typeof item.heightPx === "number" && item.heightPx > 0 ? item.heightPx : spec.heightPx;
  const customWidthPx = typeof item.widthPx === "number" && item.widthPx > 0 ? item.widthPx : null;
  const widthPx = customWidthPx ?? spec.widthPx;
  const fullWidth = customWidthPx != null ? false : !isExploreCtaPageKey(item.pageKey);
  return { widthPx, heightPx, fullWidth };
}

export function getPlacementPagesForAudience(audience: string): PageCtaPageKey[] {
  if (audience === "LOGGED_IN") return LOGGED_IN_PLACEMENT_PAGES;
  if (audience === "BOTH") return BOTH_PLACEMENT_PAGES;
  return GUEST_PLACEMENT_PAGES;
}

export function isPlacementAllowedForAudience(pageKey: string, audience: string): boolean {
  return getPlacementPagesForAudience(audience).includes(pageKey as PageCtaPageKey);
}

export function getDestinationsForAudience(audience: string): PageCtaDestination[] {
  if (audience === "LOGGED_IN") return PAGE_CTA_LOGGED_IN_DESTINATIONS;
  if (audience === "BOTH") {
    const seen = new Set<string>();
    const merged: PageCtaDestination[] = [];
    for (const dest of [...PAGE_CTA_GUEST_DESTINATIONS, ...PAGE_CTA_LOGGED_IN_DESTINATIONS]) {
      if (seen.has(dest.value)) continue;
      seen.add(dest.value);
      merged.push(dest);
    }
    return merged;
  }
  return PAGE_CTA_GUEST_DESTINATIONS;
}

export function pageCtaCorridorTabPath(audience: string): string {
  return audience === "LOGGED_IN" ? "/my-discovery?tab=corridors" : "/discover?tab=corridors";
}

export function pageCtaExpertTabPath(audience: string): string {
  return audience === "LOGGED_IN" ? "/my-discovery?tab=experts" : "/discover?tab=experts";
}

export function getPageCtaProfileKind(destinationSelect: string): PageCtaProfileKind | null {
  if (destinationSelect.includes("tab=corridors")) return "CORRIDOR";
  if (destinationSelect.includes("tab=experts")) return "EXPERT";
  return null;
}

export function buildPageCtaCorridorProfileUrl(audience: string, industry: string, headUserId: string): string {
  const industryParam = operatingIndustryToUrlSlug(industry);
  const idParam = encodeURIComponent(headUserId);
  if (audience === "LOGGED_IN") return `/ecosystem-heads/${industryParam}/${idParam}`;
  return `/discover/corridor/${industryParam}/${idParam}`;
}

export function buildPageCtaExpertProfileUrl(
  audience: string,
  opts: { industry: string | null; expertUserId: string; companyId: string | null },
): string | null {
  if (audience === "LOGGED_IN") {
    if (!opts.industry?.trim()) return null;
    return `/experts/${operatingIndustryToUrlSlug(opts.industry)}/${encodeURIComponent(opts.expertUserId)}`;
  }
  if (!opts.companyId?.trim()) return null;
  return `/discover/expert/company/${encodeURIComponent(opts.companyId)}`;
}

function decodePathSegment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function pathWithoutQuery(url: string): string {
  const q = url.indexOf("?");
  const h = url.indexOf("#");
  const end = [q, h].filter((i) => i >= 0).sort((a, b) => a - b)[0];
  return end === undefined ? url : url.slice(0, end);
}

export function matchPageCtaDestination(buttonUrl: string, audience: string): PageCtaDestinationMatch {
  const trimmed = buttonUrl.trim();
  const exact = getDestinationsForAudience(audience).find((d) => d.path && d.path === trimmed);
  if (exact) {
    return { destinationSelect: exact.value, profileId: null, profileCompanyId: null };
  }

  const path = pathWithoutQuery(trimmed);
  const guestCorridor = path.match(/^\/discover\/corridor\/([^/]+)\/([^/]+)$/);
  if (guestCorridor) {
    return {
      destinationSelect: pageCtaCorridorTabPath(audience),
      profileId: decodePathSegment(guestCorridor[2]),
      profileCompanyId: null,
    };
  }

  const loggedCorridor = path.match(/^\/ecosystem-heads\/([^/]+)\/([^/]+)$/);
  if (loggedCorridor && loggedCorridor[1] !== "sector") {
    return {
      destinationSelect: pageCtaCorridorTabPath(audience),
      profileId: decodePathSegment(loggedCorridor[2]),
      profileCompanyId: null,
    };
  }

  const guestExpert = path.match(/^\/discover\/expert\/company\/([^/]+)$/);
  if (guestExpert) {
    return {
      destinationSelect: pageCtaExpertTabPath(audience),
      profileId: null,
      profileCompanyId: decodePathSegment(guestExpert[1]),
    };
  }

  const loggedExpertCompany = path.match(/^\/experts\/company\/([^/]+)\/profile$/);
  if (loggedExpertCompany) {
    return {
      destinationSelect: pageCtaExpertTabPath(audience),
      profileId: null,
      profileCompanyId: decodePathSegment(loggedExpertCompany[1]),
    };
  }

  const loggedExpert = path.match(/^\/experts\/([^/]+)\/([^/]+)$/);
  if (loggedExpert && loggedExpert[1] !== "company") {
    return {
      destinationSelect: pageCtaExpertTabPath(audience),
      profileId: decodePathSegment(loggedExpert[2]),
      profileCompanyId: null,
    };
  }

  return { destinationSelect: PAGE_CTA_CUSTOM_DESTINATION, profileId: null, profileCompanyId: null };
}

export function matchDestinationSelectValue(buttonUrl: string, audience: string): string {
  return matchPageCtaDestination(buttonUrl, audience).destinationSelect;
}

export function firstPlacementPage(audience: string): PageCtaPageKey {
  return getPlacementPagesForAudience(audience)[0] ?? "DISCOVER";
}

export type PageCtaPublicItem = {
  id: string;
  pageKey: string;
  slotKey: string;
  layout: PageCtaLayout;
  title: string;
  description: string;
  buttonLabel: string;
  buttonUrl: string;
  audience?: string;
  heightPx: number | null;
  widthPx: number | null;
  buttonHeightPx: number | null;
  buttonWidthPx: number | null;
  imageHeightPx: number | null;
  imageWidthPx: number | null;
  buttonXPct: number | null;
  buttonYPct: number | null;
  mobileButtonXPct: number | null;
  mobileButtonYPct: number | null;
  titleFontSize: number | null;
  titleFontWeight: number | null;
  titleColor: string | null;
  descriptionFontSize: number | null;
  descriptionColor: string | null;
  buttonFontSize: number | null;
  buttonTextColor: string | null;
  buttonBackgroundColor: string | null;
  backgroundColor: string;
  imageUrl: string | null;
  mobileImageUrl: string | null;
};
